import { useEffect, useState } from "react";
import { Trash2, Mail, Phone, Users, MapPin, CakeSlice, Paperclip } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function AdminInquiries() {
    const [items, setItems] = useState([]);

    const load = () => api.get("/inquiries").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try { await api.put(`/inquiries/${id}/status?status=${status}`); load(); }
        catch { toast.error("Update failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this inquiry?")) return;
        try { await api.delete(`/inquiries/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="admin-inquiries">
            <h2 className="font-display text-3xl mb-6">Inquiries ({items.length})</h2>

            <div className="space-y-3">
                {items.map((q, i) => (
                    <div key={q.id} className="bg-white border border-[#EAE3DE] p-5" data-testid={`inquiry-row-${i}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 flex-wrap mb-2">
                                    <p className="font-display text-lg">{q.name}</p>
                                    <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${
                                        q.status === "new" ? "bg-blush text-white" : "bg-[#EAE3DE] text-ink"
                                    }`}>{q.status}</span>
                                    {q.cake_name && <span className="text-xs text-ink-soft">for {q.cake_name}</span>}
                                    {q.product_name && <span className="text-xs text-ink-soft">for {q.product_name}{q.qty_choice ? ` (qty ${q.qty_choice})` : ""}</span>}
                                    {q.size_label && <span className="text-xs text-ink-soft">— {q.size_label}{q.size_price != null ? ` ($${q.size_price})` : ""}</span>}
                                    {q.flavour_name && <span className="text-xs text-ink-soft">for {q.flavour_name} flavour</span>}
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-soft mb-3">
                                    {q.email && <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1 hover:text-blush"><Mail size={12} /> {q.email}</a>}
                                    {q.phone && <a href={`tel:${q.phone}`} className="inline-flex items-center gap-1 hover:text-blush"><Phone size={12} /> {q.phone}</a>}
                                    {q.event_date && <span>Event: {q.event_date}</span>}
                                    {q.guests && <span className="inline-flex items-center gap-1"><Users size={12} /> {q.guests} guests</span>}
                                    {q.venue && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {q.venue}</span>}
                                    {q.fulfillment_type && <span>{q.fulfillment_type}</span>}
                                    {q.portion_type && <span className="inline-flex items-center gap-1"><CakeSlice size={12} /> {q.portion_type} portions</span>}
                                    {q.flavour_choice && <span className="inline-flex items-center gap-1"><CakeSlice size={12} /> {q.flavour_choice}</span>}
                                    {(q.cake_slab_serves || q.cake_slab_type || q.cake_slab_guests) && (
                                        <span className="inline-flex items-center gap-1 text-blush">
                                            <CakeSlice size={12} /> Slab: {q.cake_slab_serves || "—"} serves ({q.cake_slab_type || "—"}){q.cake_slab_guests ? `, ${q.cake_slab_guests} guests` : ""}
                                        </span>
                                    )}
                                    <span>{new Date(q.created_at).toLocaleString()}</span>
                                </div>
                                {q.inspiration_url && (
                                    <a href={q.inspiration_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blush hover:underline mb-3">
                                        <Paperclip size={12} /> View inspiration image
                                    </a>
                                )}
                                {q.message && <p className="text-sm text-ink whitespace-pre-line">{q.message}</p>}
                            </div>
                            <div className="flex flex-col gap-2 flex-shrink-0">
                                <select value={q.status} onChange={(e) => updateStatus(q.id, e.target.value)} className="text-xs px-2 py-1 border border-[#EAE3DE]" data-testid={`inquiry-status-${i}`}>
                                    <option value="new">New</option>
                                    <option value="contacted">Contacted</option>
                                    <option value="quoted">Quoted</option>
                                    <option value="booked">Booked</option>
                                    <option value="closed">Closed</option>
                                </select>
                                <button onClick={() => remove(q.id)} className="text-ink-soft hover:text-blush" data-testid={`delete-inquiry-${i}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <p className="text-center text-ink-soft py-12">No inquiries yet.</p>}
            </div>
        </div>
    );
}
